/** Data hooks of the catalogue (1.N-2 deliverable 2): reads only. */
import { catalogueQuery, typesList } from '@codex/contracts';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { api } from '../../auth/client.js';
import { type CatalogueView, PAGE_SIZE, queryKey, queryOf } from '../model/view.js';

export const typesKey = ['types'] as const;
export const catalogueKey = ['catalogue'] as const;

export function useTypes() {
  return useQuery({
    queryKey: typesKey,
    queryFn: async () => (await api(typesList)).items,
    retry: false,
  });
}

/** The query key of one type's view. */
export function rowsKey(typeKey: string, view: CatalogueView) {
  return [...catalogueKey, typeKey, queryKey(view, typeKey)] as const;
}

export function useCatalogueRows(typeKey: string, view: CatalogueView, enabled: boolean) {
  return useInfiniteQuery({
    queryKey: rowsKey(typeKey, view),
    enabled,
    // Always stale: a mount, or a switch to a cached view, asks the server again while the cached
    // rows show at once. Live events reach only a mounted catalogue page, so writes made on
    // document pages would otherwise stay hidden for the shell's 30 s (catalogue-npc-by-faction).
    staleTime: 0,
    retry: false,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api(catalogueQuery, {
        params: { typeKey },
        body: {
          ...queryOf(view),
          limit: PAGE_SIZE,
          ...(pageParam === undefined ? {} : { cursor: pageParam }),
        },
      }),
    getNextPageParam: (last) => last.next ?? undefined,
  });
}
